import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { isBrowser, isMobile } from "react-device-detect";
import { Box, Button, Typography, TextField, Divider, FormControl, Select, MenuItem, IconButton, CircularProgress, useTheme } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getElementsDataApi, updateElementApi } from "../../../api/model.js";
import { getTasksDataApi } from "../../../api/schedule.js";
import { getCommentsDetailsApi, resolveCommentsApi } from "../../../api/comment.js";
import { getStatusIcon, getStatusString } from "../../../utils/schedule.js";
import { NotificationContext } from "../../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import DatePicker from "../../../component/DatePicker.js";
import Popup from "../../../component/popup/Popup.js";
import CommentsList from "./CommentsList.js";
import ConfirmPopup from "../../../component/popup/ConfirmPopup.js";
import CommentsDetailsPopup from "./CommentsDetailsPopup.js";
import AddCommentPopup from "./AddCommentPopup.js";
import { SettingsPowerRounded } from "@mui/icons-material";

const ItemDetailsPopup = ({ selectedElement, open, onClose, taskIdOptions, QTOIdOptions, tasks, onCommentAdded, onCommentResolve, user }) => {
  const theme = useTheme();
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [commentsDetailsPopup, setCommentsDetailsPopup] = useState(false);
  const [addCommentsPopup, setAddCommentsPopup] = useState(false);
  const [isPreview, setIsPreview] = useState(isMobile);
  const [isConfirming, setIsConfirming] = useState(false);
  const [commentIDFromList, setCommentIDFromList] = useState(null);
  const [elementProperties, setElementProperties] = useState({
    itemName: "",
    bimElementId: "",
    workset: "",
    contractor: "",
    volume: null,
    length: null,
    quantity: "",
    qtoId: "",
    taskId: "",
    taskName: "",
    workType: "",
    planStartDate: null,
    planEndDate: null,
    actualStartDate: null,
    elementActualStartDate: null,
    actualEndDate: null,
    elementActualEndDate: null,
    // actualCost: "",
    status: "WIP",
  });

  const ITEM_DETAILS_PREVIEW = [
    { label: "Item Name", name: "itemName", type: "text", editable: false },
    { label: "Item ID", name: "bimElementId", type: "text", editable: false },
    { label: "Work Type", name: "workType", type: "text", editable: false },
    { label: "Status", name: "status", type: "symbol", editable: false },
    {
      label: "Plan Start",
      name: "planStartDate",
      type: "date",
      editable: false,
    },
    { label: "Plan End", name: "planEndDate", type: "date", editable: false },
  ];

  const ITEM_DETAILS = [
    { label: "Item Name", name: "itemName", type: "text", editable: false },
    { label: "Item ID", name: "bimElementId", type: "text", editable: false },
    { label: "Workset", name: "workset", type: "text", editable: false },
    { label: "Contractor", name: "contractor", type: "text", editable: false },
    { label: "Volume", name: "volume", type: "number", editable: false },
    { label: "Length", name: "length", type: "number", editable: false },
    {
      label: "QTO ID",
      name: "qtoId",
      type: "select",
      editable: true,
      options: QTOIdOptions,
      placeholder: "Select a QTO ID...",
    },
    {
      label: "Task ID",
      name: "taskId",
      type: "select",
      editable: true,
      options: taskIdOptions,
      placeholder: "Select a Task ID...",
    },
    { label: "Task Name", name: "taskName", type: "text", editable: false },
    { label: "Work Type", name: "workType", type: "text", editable: false },
    {
      label: "Plan Start",
      name: "planStartDate",
      type: "date",
      editable: false,
    },
    { label: "Plan End", name: "planEndDate", type: "date", editable: false },
    {
      label: "Actual Start",
      name: "actualStartDate",
      type: "date",
      editable: true,
    },
    {
      label: "Actual End",
      name: "actualEndDate",
      type: "date",
      editable: true,
    },
    // {
    //   label: "Actual Cost",
    //   name: "actualCost",
    //   type: "number",
    //   editable: true,
    // },
    { label: "Status", name: "status", type: "symbol", editable: true },
  ];

  const readonly = !user?.editRight;

  useEffect(() => {
    if (selectedElement) {
      getData();
    }
  }, [selectedElement]);

  const formatDate = (date) => {
    return date && dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : null;
  };

  const formatDatePriority = (primaryDate, secondaryDate) => {
    return primaryDate && dayjs(primaryDate).isValid()
      ? dayjs(primaryDate).format("YYYY-MM-DD")
      : secondaryDate && dayjs(secondaryDate).isValid()
      ? dayjs(secondaryDate).format("YYYY-MM-DD")
      : null;
  };

  const getData = async () => {
    setIsLoading(true);
    const result = await getElementsDataApi(projectId, selectedElement.bimElementId);
    if (result.data.length) {
      const elementData = result.data[0];
      if (elementData.taskId) {
        const taskResult = await getTasksDataApi(projectId, elementData.taskId);
        if (!taskResult.data.length) {
          setElementProperties({
            ...elementData,
            length: elementData && elementData.length ? elementData.length.toFixed(2) : null,
            volume: elementData && elementData.volume ? elementData.volume.toFixed(2) : null,
            taskName: "",
            planStartDate: null,
            planEndDate: null,
            actualStartDate: null,
            actualEndDate: null,
            workType: "",
            status: "",
            quantity: elementData && elementData.quantity ? elementData.quantity : 0,
            qtoId: elementData && elementData.qtoId ? elementData.qtoId : "",
          });
        } else {
          const taskData = taskResult?.data[0];
          setElementProperties({
            ...elementData,
            length: elementData && elementData.length ? elementData.length.toFixed(2) : null,
            volume: elementData && elementData.volume ? elementData.volume.toFixed(2) : null,
            taskName: taskData ? taskData.taskName : "",
            planStartDate: formatDatePriority(elementData.planStartDate, taskData.planStart),
            planEndDate: formatDatePriority(elementData.planEndDate, taskData.planEnd),
            actualStartDate: formatDatePriority(elementData.actualStartDate, taskData.actualStart),
            actualEndDate: formatDatePriority(elementData.actualEndDate, taskData.actualEnd),
            elementActualStartDate: formatDate(elementData.actualStartDate),
            elementActualEndDate: formatDate(elementData.actualEndDate),
            workType: taskData ? taskData.workType : "",
            status: taskData ? taskData.status : "",
            quantity: elementData && elementData.quantity ? elementData.quantity : 0,
            qtoId: elementData && elementData.qtoId ? elementData.qtoId : "",
          });
        }
      } else {
        setElementProperties({
          ...elementData,
          taskName: "",
          planStartDate: null,
          planEndDate: null,
          actualStartDate: null,
          actualEndDate: null,
          workType: "",
          status: "",
          quantity: elementData && elementData.quantity ? elementData.quantity : 0,
          qtoId: elementData && elementData.qtoId ? elementData.qtoId : "",
        });
      }
      if (!QTOIdOptions.map((q) => q.label).includes(elementData?.qtoId) && elementData.qtoId) {
        showError("QTO ID does not exist in the project.");
      }
    }
    const commentResult = await getCommentsDetailsApi(projectId, selectedElement.bimElementId);
    setComments(commentResult?.data);
    setIsLoading(false);
  };

  const handleSaveUpdate = async () => {
    try {
      const response = await updateElementApi(projectId, elementProperties);
      if (response.data) {
        showSuccess("Item update saved", "assets/icons/greencomment.svg");
        setIsConfirming(false);
      } else {
        showError("Update failed.");
      }
    } catch {
      showError("Update failed.");
    }
  };

  const handleDateChange = (name, date) => {
    setElementProperties((prevValues) => ({
      ...prevValues,
      [name]: date ? date.format("YYYY-MM-DD") : null,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (value === "clear") {
      if (name === "taskId") {
        setElementProperties((prevValues) => ({
          ...prevValues,
          taskId: "",
          taskName: "",
          workType: "",
          planStartDate: null,
          planEndDate: null,
          actualStartDate: elementProperties.elementActualStartDate,
          actualEndDate: elementProperties.elementActualEndDate,
          // actualCost: "",
          status: "",
        }));
      } else {
        setElementProperties((prevValues) => ({
          ...prevValues,
          [name]: "",
        }));
      }
    } else if (name === "taskId") {
      const selectedTask = tasks.find((task) => task.taskId === value);
      setElementProperties((prevValues) => ({
        ...prevValues,
        [name]: value,
        taskName: selectedTask ? selectedTask.taskName : "",
        planStartDate: formatDate(selectedTask?.planStart),
        planEndDate: formatDate(selectedTask?.planEnd),
        actualStartDate: selectedTask ? elementProperties.elementActualStartDate || formatDate(selectedTask.actualStart) : null,
        actualEndDate: selectedTask ? elementProperties.elementActualEndDate || formatDate(selectedTask.actualEnd) : null,
        workType: selectedTask ? selectedTask.workType : "",
        status: selectedTask ? selectedTask.status : "",
      }));
    } else {
      setElementProperties((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    }
  };

  const handleResolveComment = async (commentId) => {
    try {
      setIsResolving(true);
      //resolve comment
      await resolveCommentsApi(projectId, commentId);
      //reload comments when resolve
      const commentResult = await getCommentsDetailsApi(projectId, selectedElement.bimElementId);
      onCommentResolve();
      showSuccess("The comment has been successfully resolved.");
      setComments(commentResult?.data);
    } catch {
      showError("Resolve failed.");
    } finally {
      setIsResolving(false);
    }
  };

  const handleAddComment = async () => {
    try {
      const commentResult = await getCommentsDetailsApi(projectId, selectedElement.bimElementId);
      onCommentAdded();
      setAddCommentsPopup(false);
      setComments(commentResult?.data);
    } catch {
      showError("An error occured.");
    }
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "Complete":
        return {
          background: "#00B176",
          color: "#fff",
        };
      case "WIP":
        return {
          background: "#FFD900",
        };
      case "Not Start":
        return {
          background: "#fff",
          border: "1px solid #717273",
        };
      case "WIP(Delay)":
        return {
          background: "#ff2121",
        };
      case "Not Start(Delay)":
        return {
          background: "#fff",
          border: "1px solid rgb(255, 255, 255)",
        };
      default:
        return {
          background: "#fff",
          border: `1px solid ${theme.palette.text.disabled}`,
          color: theme.palette.text.disabled,
        };
    }
  };

  return (
    <Popup
      open={open}
      onClose={onClose}
      title="Item Details"
      noModal
      isExpanded={isBrowser ? false : !isPreview}
      onMinimize={() => setIsPreview(true)}
      popupStyle={
        isBrowser
          ? {
              maxWidth: "380px",
              minWidth: "380px",
              left: "auto",
              right: "45px",
              top: "35px",
              maxHeight: "calc(100vh - 148px)",
              transform: "none",
              overflow: "auto",
            }
          : isPreview
          ? {
              maxWidth: "100%",
              minWidth: "100%",
              top: undefined,
              bottom: 0,
              left: 0,
              height: 190,
              transform: "none",
              overflow: "auto",
            }
          : {}
      }
    >
      {!isLoading && !isPreview && (
        <Box sx={{ maxHeight: "calc(100dvh - 50px)", overflow: "auto" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {ITEM_DETAILS.map((detail, index) => {
              const isTaskIdEmpty = !elementProperties["taskId"];
              const fieldsToHide = ["Task Name", "Work Type", "Plan Start", "Plan End", "Actual Start", "Actual End", "Actual Cost", "Status"];
              const shouldRenderDetail = !(isTaskIdEmpty && fieldsToHide.includes(detail.label));
              return (
                shouldRenderDetail && (
                  <Box key={`${detail.name}-${index}`}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 0.2,
                        px: isMobile ? 2 : 4,
                      }}
                    >
                      <Typography component="span" flex={1}>
                        {detail.label}
                      </Typography>
                      {detail.type === "date" ? (
                        <DatePicker
                          label=""
                          value={elementProperties[detail.name] ? dayjs(elementProperties[detail.name]) : null}
                          onChange={(date) => detail.editable && handleDateChange(detail.name, date)}
                          disabled={!detail.editable || readonly}
                        />
                      ) : detail.type === "text" || detail.type === "number" ? (
                        <TextField
                          variant="outlined"
                          fullWidth
                          value={elementProperties[detail.name]}
                          onChange={detail.editable ? handleInputChange : undefined}
                          name={detail.name}
                          disabled={!detail.editable || readonly}
                          placeholder="---"
                          size="small"
                          type={detail.type}
                          sx={{
                            flex: 1.5,
                            "& .MuiInputBase-input": {
                              px: 1,
                              py: 0.3,
                              fontWeight: 600,
                            },
                            "& .MuiOutlinedInput-input.Mui-disabled": {
                              WebkitTextFillColor: "#000",
                            },
                            "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
                              borderColor: "transparent!important",
                            },
                          }}
                        />
                      ) : detail.type === "select" ? (
                        <FormControl variant="outlined" fullWidth sx={{ flex: 1.5 }}>
                          <Select
                            value={elementProperties[detail.name]}
                            onChange={(event) => detail.editable && handleInputChange(event)}
                            name={detail.name}
                            disabled={!detail.editable || readonly}
                            renderValue={(selected) => {
                              if (!selected || selected?.length === 0) {
                                return (
                                  <div
                                    style={{
                                      color: "#98a1a9",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {detail.placeholder}
                                  </div>
                                );
                              }
                              if (detail.name === "taskId") {
                                return (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    {selected}
                                    {getStatusIcon(elementProperties.status)}
                                  </Box>
                                );
                              } else {
                                return selected;
                              }
                            }}
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-input": {
                                py: 0.3,
                                px: 1,
                                fontWeight: 600,
                              },
                            }}
                            displayEmpty
                          >
                            <MenuItem key="clear" value="clear">
                              - - - CLEAR SELECTION - - -
                            </MenuItem>
                            {detail.options?.map((option, optionIndex) => (
                              <MenuItem key={`${detail.name}-${option.value}-${optionIndex}`} value={option.value}>
                                {option.value}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Box
                          sx={{
                            flex: 1.5,
                            height: "16px",
                            borderRadius: "10px",
                            textAlign: "center",
                            lineHeight: "16px",
                            ...getStatusStyles(
                              getStatusString(
                                elementProperties.planStartDate,
                                elementProperties.planEndDate,
                                elementProperties.actualStartDate,
                                elementProperties.actualEndDate,
                                new Date()
                              )
                            ),
                          }}
                        >
                          {getStatusString(
                            elementProperties.planStartDate,
                            elementProperties.planEndDate,
                            elementProperties.actualStartDate,
                            elementProperties.actualEndDate,
                            new Date()
                          ) || "---"}
                        </Box>
                      )}
                    </Box>
                    {detail.name === "qtoId" && <Divider variant="fullWidth" sx={{ my: 1 }} />}
                  </Box>
                )
              );
            })}
          </LocalizationProvider>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="contained"
              color={readonly ? "primary" : "success"}
              sx={{ py: "2px", minWidth: "200px", borderRadius: "10px" }}
              onClick={() => setIsConfirming(true)}
              disabled={readonly}
            >
              Save Update
            </Button>
          </Box>
          <Divider variant="fullWidth" sx={{ my: 1 }} />
          <Box display="flex" flexDirection="row" justifyContent="center" alignItems="center" sx={{ p: 1, flexGrow: 1 }}>
            <Box width={"80%"}>
              <Divider>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#717273",
                  }}
                >
                  <span>
                    Comments ({comments?.filter((c) => c.status === "Resolved").length}/{comments?.length})
                  </span>
                </Typography>
              </Divider>
            </Box>
            <IconButton
              sx={{ cursor: "pointer", position: "static", right: "12px" }}
              onClick={() => {
                setCommentsDetailsPopup(true);
              }}
            >
              <img src="assets/icons/expand.svg" alt="Expand All" style={{ height: "16px" }} />
            </IconButton>
          </Box>
          <Box px={isMobile ? 2 : 4} position="relative">
            <Box
              sx={{
                maxHeight: isBrowser ? 280 : "calc(100dvh - 40px - 580px)",
                minHeight: 100,
                overflow: "auto",
                pb: 1,
              }}
            >
              {isResolving ? (
                <Box
                  sx={{
                    height: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                comments?.map((comment, index) => (
                  <CommentsList
                    key={index}
                    comment={comment}
                    index={index}
                    onCommentResolved={handleResolveComment}
                    user={user}
                    compress
                    onOpenCommentsDetails={(id) => {
                      setCommentsDetailsPopup(true);
                      setCommentIDFromList(id); //jump this comment details
                    }}
                  />
                ))
              )}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              <Button
                onClick={() => {
                  setAddCommentsPopup(true);
                }}
                variant="contained"
                color={readonly ? "primary" : "secondary"}
                sx={{ py: "2px", minWidth: "200px", borderRadius: "10px" }}
                disabled={readonly}
              >
                Add Comment
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {!isLoading && isPreview && (
        <Box sx={{ position: "relative" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {ITEM_DETAILS_PREVIEW.map((detail, index) => {
              return (
                <Box key={`${detail.name}-${index}`}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 0.2,
                      pl: 4,
                      pr: "100px",
                    }}
                  >
                    <Typography component="span" flex={1}>
                      {detail.label}
                    </Typography>
                    {detail.type === "date" ? (
                      <DatePicker
                        label=""
                        value={elementProperties[detail.name] ? dayjs(elementProperties[detail.name]) : null}
                        onChange={(date) => detail.editable && handleDateChange(detail.name, date)}
                        disabled={!detail.editable}
                      />
                    ) : detail.type === "text" ? (
                      <TextField
                        variant="outlined"
                        fullWidth
                        value={elementProperties[detail.name]}
                        onChange={detail.editable ? handleInputChange : undefined}
                        name={detail.name}
                        disabled={!detail.editable}
                        placeholder="---"
                        size="small"
                        sx={{
                          flex: 1.5,
                          "& .MuiInputBase-input": {
                            px: 1,
                            py: 0.3,
                            fontWeight: 600,
                          },
                          "& .MuiOutlinedInput-input.Mui-disabled": {
                            WebkitTextFillColor: "#000",
                          },
                          "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
                            borderColor: "transparent!important",
                          },
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          flex: 1.5,
                          height: "16px",
                          borderRadius: "10px",
                          textAlign: "center",
                          lineHeight: "16px",
                          ...getStatusStyles(
                            getStatusString(
                              elementProperties.planStartDate,
                              elementProperties.planEndDate,
                              elementProperties.actualStartDate,
                              elementProperties.actualEndDate,
                              new Date()
                            )
                          ),
                        }}
                      >
                        {getStatusString(
                          elementProperties.planStartDate,
                          elementProperties.planEndDate,
                          elementProperties.actualStartDate,
                          elementProperties.actualEndDate,
                          new Date()
                        ) || "---"}
                      </Box>
                    )}
                  </Box>
                  {detail.name === "qtoId" && <Divider variant="fullWidth" sx={{ my: 1 }} />}
                </Box>
              );
            })}
          </LocalizationProvider>
          <Button
            variant="contained"
            color={readonly ? "primary" : "secondary"}
            sx={{
              position: "absolute",
              minWidth: 55,
              height: 55,
              borderRadius: 55 / 2,
              p: 0,
              right: 10,
              bottom: 10,
            }}
            onClick={() => {
              setIsPreview(false);
            }}
          >
            <img src={"assets/icons/edit.svg"} alt={"edit"} />
          </Button>
        </Box>
      )}
      {isLoading && (
        <Box
          sx={{
            height: "calc(100dvh - 76px - 120px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <CommentsDetailsPopup
        open={commentsDetailsPopup}
        onClose={() => {
          setCommentsDetailsPopup(false);
          setCommentIDFromList(null);
        }}
        onCommentResolved={handleResolveComment}
        isResolving={isResolving}
        comments={comments}
        user={user}
        currentCommentID={commentIDFromList}
      />
      <AddCommentPopup
        open={addCommentsPopup}
        onClose={() => {
          setAddCommentsPopup(false);
        }}
        selectedElement={selectedElement}
        onCommentAdded={handleAddComment}
      />
      <ConfirmPopup
        open={isConfirming}
        message="Confirm to update this element?"
        onCancel={() => setIsConfirming(false)}
        onConfirm={handleSaveUpdate}
      ></ConfirmPopup>
    </Popup>
  );
};

export default ItemDetailsPopup;
